import { LinerDrawingReference, LinerLocation } from "features/organizations/asset-sections/models/asset-section-models"
import { CssSVGClasses } from "../editor-utils/editor-types"
import { GetLinerId } from "../editor-utils/id-string-utils"
import { GetLinerLocationInfo } from "./wear-reference-plane-helper"

/**
 * Assigns incremental IDs to references of liner paths within an SVG document and updates a React state with these assignments.
 *
 * This function:
 * 1. Identifies liners from both the input `svgDocument` and `renderedSvg`.
 * 2. Checks if a liner in the `svgDocument` has a reference class (`ws-reference`). If not, it adds this class.
 * 3. Updates the provided React state (`setSelectedLinersRef`) with an object mapping the old liner ID to the new incremental ID.
 *
 * Liners are identified based on their ID containing the term 'liner', not containing 'hole', and having the class 'ws-liner'.
 *
 * @param {Document} svgDocument - The SVG document used as a reference.
 * @param {SVGSVGElement} renderedSvg - The SVG element that is rendered in the DOM.
 * @param {React.Dispatch<React.SetStateAction<LinerIDPair[]>>} setSelectedLinersRef - The React state setter function to update with the new IDs.
 */
export function AssignIncrementalIdsToDrawingReferencesToLiners(
    linerLocations: LinerLocation[],
    svgDocument: Document,
    renderedSvg: SVGSVGElement,
    selectedLinersDrawingRef: LinerDrawingReference[],
    setSelectedLinersDrawingRef: (liners: LinerDrawingReference[]) => void,
): void {
    try {
        const docAllPaths = Array.from(svgDocument.querySelectorAll<SVGPathElement>("path"))
        const allPaths = Array.from(renderedSvg.querySelectorAll<SVGPathElement>("path"))
        const docLiners = docAllPaths.filter(path => path.classList.contains(CssSVGClasses.WsLiner))
        const liners = allPaths.filter(path => path.classList.contains(CssSVGClasses.WsLiner))
        let updatedLiners = [...selectedLinersDrawingRef]
        liners.forEach(liner => {
            const linerIdPart = GetLinerId(liner.id)
            if (!linerIdPart) return

            const linerLocationInfo = GetLinerLocationInfo(linerLocations, linerIdPart)
            const docLinerPart = docLiners.filter(path => {
                return path.id === linerIdPart
            })

            if (!docLinerPart[0]?.classList.contains(CssSVGClasses.WsReferenceTemp)) docLinerPart[0].classList.add(CssSVGClasses.WsReferenceTemp)

            if (linerIdPart) {
                const existingRefIndex = updatedLiners.findIndex(entry => entry.linerReference === linerIdPart)
                const newRefDI = `DWPS-${linerIdPart}`

                if (existingRefIndex !== -1) {
                    updatedLiners[existingRefIndex].newDrawingReferenceName = newRefDI
                } else {
                    updatedLiners.push({
                        linerLocationId: linerLocationInfo?.id ?? "",
                        linerReference: linerIdPart,
                        newDrawingReferenceName: newRefDI,
                    })
                }
            }
        })
        setSelectedLinersDrawingRef(updatedLiners)
    } catch (e) {
        console.error("Error happen in AssignIncrementalIdsToReferencesToLiners", e)
    }
}

export const GetLinerLocationName = (linerLocations: LinerLocation[], pathId: string): string => {
    try {
        const linerName = GetLinerId(pathId) as string
        const backEndWearReferencePlane = linerLocations?.find(l => l.reference?.toLocaleLowerCase() === linerName?.toLocaleLowerCase())?.name ?? ""

        if (backEndWearReferencePlane) {
            return backEndWearReferencePlane
        } else {
            return ""
        }
    } catch (e) {
        console.error("Error happen in GetLinerLocationName", e)
        return ""
    }
}
